// src/api/reportApi.js

const parseJsonSafe = async (res) => {
    try { return await res.json(); } catch { return null; }
};

const parseTextSafe = async (res) => {
    try { return await res.text(); } catch { return ""; }
};

const buildError = (message, validation = null) => {
    const err = new Error(message);
    if (validation) err.validation = validation;
    return err;
};

const FIELD_ALIASES = {
    token: "personalAccessToken",
    personalAccessToken: "personalAccessToken",
    repo: "repo",
    repoName: "repo",
    repoPathOrUrl: "repoPathOrUrl",
    url: "repoPathOrUrl",
    branch: "branch",
    email: "email",
    general: "general"
};

const normalizeFieldErrors = (errors) =>
    Object.fromEntries(
        Object.entries(errors || {}).map(([key, value]) => [
            FIELD_ALIASES[key] || key,
            value
        ])
    );


const normalizeBackendError = async (res) => {
    const text = await parseTextSafe(res);

    if (text) {
        let data;

        try {
            data = JSON.parse(text);
        } catch {
            throw buildError(text);
        }

        if (data?.errors && typeof data.errors === "object")
            throw buildError("Validation failed", normalizeFieldErrors(data.errors));

        if (data?.message) throw buildError(data.message);
        if (data?.error) throw buildError(data.error);

        throw buildError(text);
    }

    const messages = {
        400: "Validation failed",
        401: "Authentication required",
        403: "Access denied or invalid token",
        404: "Repository or branch not found",
        409: "Analysis already running",
        429: "Rate limit exceeded",
        500: "Internal server error"
    };

    throw buildError(messages[res.status] || `Request failed (${res.status})`);
};

const TOKEN_KEY = "custor.auth.token";
const USER_KEY = "custor.auth.user";

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const getCurrentUser = () => {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
};

export const setAuth = (data) => {
    if (!data?.token) throw buildError("Login response does not contain token");

    localStorage.setItem(TOKEN_KEY, data.token);

    if (data.email) {
        const current = getCurrentUser() || {};
        localStorage.setItem(USER_KEY, JSON.stringify({
            email: data.email,
            nickname: data.nickname ?? current.nickname ?? null,
            plan: data.plan ?? current.plan ?? null,
            role: data.role ?? current.role ?? null
        }));
    }
};

export const clearAuth = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem("custor.currentJob");
    window.dispatchEvent(new Event("custor-auth-logout"));
};

const authFetch = async (url, options = {}) => {
    const headers = new Headers(options.headers || {});
    const token = getToken();

    if (token) headers.set("Authorization", `Bearer ${token}`);

    return fetch(url, {
        ...options,
        headers,
        cache: options.cache || "no-store"
    });
};

export const login = async (email, password) => {
    let res;
    try {
        res = await fetch("/auth/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password })
        });
    } catch {
        throw buildError("Server unavailable");
    }

    if (!res.ok) await normalizeBackendError(res);

    const data = await parseJsonSafe(res);
    if (!data) throw buildError("Empty server response");

    setAuth(data);
    return data;
};

export const register = async (loginValue, email, password) => {
    let res;
    try {
        res = await fetch("/auth/register", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ login: loginValue, email, password })
        });
    } catch {
        throw buildError("Server unavailable");
    }

    if (!res.ok) await normalizeBackendError(res);
    return { message: await parseTextSafe(res) };
};

export const verifyEmail = async (token) => {
    let res;
    try {
        res = await fetch(
            `/auth/verify-email?token=${encodeURIComponent(token)}`,
            { method: "GET" }
        );
    } catch {
        throw buildError("Server unavailable");
    }

    if (!res.ok) await normalizeBackendError(res);
    return parseTextSafe(res);
};

export const resendVerification = async (email) => {
    let res;
    try {
        res = await fetch("/auth/resend-verification", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email })
        });
    } catch {
        throw buildError("Server unavailable");
    }

    if (!res.ok) await normalizeBackendError(res);
    return parseTextSafe(res);
};

export const fetchCurrentUser = async () => {
    const user = getCurrentUser();
    if (!user?.email) throw buildError("Authentication required");

    const res = await authFetch(
        `/auth/user/${encodeURIComponent(user.email)}`
    );

    if (!res.ok) {
        if (res.status === 401 || res.status === 403) clearAuth();
        await normalizeBackendError(res);
    }

    const text = await parseTextSafe(res);
    if (!text) throw buildError("Empty server response");

    try {
        return JSON.parse(text);
    } catch {
        throw buildError(text);
    }
};

const safeFetch = async (url) => {
    try {
        const res = await authFetch(url);

        if (!res.ok) {
            if (res.status === 401 || res.status === 403) clearAuth();
            console.warn("Fetch failed:", url, res.status);
            return null;
        }

        const text = await res.text();
        return text ? JSON.parse(text) : null;
    } catch (e) {
        console.warn("Fetch error:", url, e.message);
        return null;
    }
};

export const loadHistory = () => safeFetch("/repo/history");

export const loadDashboard = async (jobId) => {
    const res = await authFetch(`/repo/${jobId}/dashboard`);

    if (!res.ok) {
        if (res.status === 401 || res.status === 403) clearAuth();
        throw new Error(`Dashboard request failed: ${res.status}`);
    }

    const data = await parseJsonSafe(res);
    if (!data) throw new Error("Empty dashboard response");

    return data;
};

export const loadJobStatus = async (jobId) => {
    const res = await authFetch(`/jobs/${jobId}/status`);

    if (res.status === 404) return { status: "NOT_FOUND" };

    if (res.status === 401 || res.status === 403) {
        clearAuth();
        throw buildError("Authentication required");
    }

    if (!res.ok) {
        throw buildError(`Job status request failed: ${res.status}`);
    }

    return parseJsonSafe(res);
};

export const startAnalysis = async (payload) => {
    let res;
    try {
        res = await authFetch("/repo/analyze", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
        });
    } catch {
        throw buildError("Server unavailable");
    }

    if (!res.ok) {
        if (res.status === 401) clearAuth();
        await normalizeBackendError(res);
    }

    const data = await parseJsonSafe(res);
    if (!data) throw buildError("Empty server response");

    if (data.errors)
        throw buildError("Validation failed", normalizeFieldErrors(data.errors));

    if (data.status === "error" || data.success === false)
        throw buildError(data.message || data.error || "Failed to start analysis");

    if (!data.jobId)
        throw buildError(data.message || "Analysis failed: Job ID missing");

    return data;
};

export async function runAgent(jobId, task) {
    if (!jobId) throw new Error("No job selected");
    if (!task?.trim()) throw new Error("Agent task is required");

    const response = await authFetch(`/repo/${jobId}/agents`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            task: task.trim(),
            maxSteps: 10,
            maxToolCalls: 4,
            timeoutMs: 240000
        })
    });

    if (!response.ok) {
        if (response.status === 401) {
            clearAuth();
            throw new Error("Unauthorized");
        }

        if (response.status === 403)
            throw new Error("Agent is not available for your current plan");

        const text = await response.text();
        throw new Error(text || `Agent request failed: ${response.status}`);
    }

    return response.json();
}